import React, { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon, Loader2, Crop as CropIcon } from 'lucide-react';
import Button from './Button';
import ImageCropModal from './ImageCropModal';

interface HeroImageUploadProps {
  currentImageUrl?: string | null;
  onImageChange: (imageUrl: string | null) => void;
  onImageUpload: (file: File) => Promise<string>;
  onImageRemove?: () => Promise<void>;
  disabled?: boolean;
  className?: string;
  enableCrop?: boolean;
}

export function HeroImageUpload({ 
  currentImageUrl,
  onImageChange,
  onImageUpload,
  onImageRemove,
  disabled = false,
  className = '',
  enableCrop = true
}: HeroImageUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // File validation constants - optimized for hero images
  const MAX_FILE_SIZE = 6 * 1024 * 1024; // 6MB for hero images
  const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
  const RECOMMENDED_DIMENSIONS = { width: 1200, height: 400 };

  const validateFile = (file: File): string | null => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return 'Invalid file type. Please upload JPG, PNG, or WebP images only.';
    }
    
    if (file.size > MAX_FILE_SIZE) {
      return 'File too large. Maximum size is 6MB.';
    }
    
    return null;
  };

  const handleFile = async (file: File) => {
    setError(null);
    
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    if (enableCrop) {
      // Show crop modal for user to crop the image
      setPendingFile(file);
      setShowCropModal(true);
    } else {
      // Upload directly without cropping
      await uploadFile(file);
    }
  };

  const uploadFile = async (file: File) => {
    setIsUploading(true);
    
    try {
      const imageUrl = await onImageUpload(file);
      onImageChange(imageUrl);
    } catch (error: unknown) {
      console.error('Hero image upload failed:', error);
      setError(error instanceof Error ? error.message : 'Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleCropComplete = async (croppedFile: File) => {
    setShowCropModal(false);
    setPendingFile(null);
    await uploadFile(croppedFile);
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    setPendingFile(null);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFile(file);
    }
    // Reset input value to allow re-uploading the same file
    e.target.value = '';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !isUploading) {
      setDragActive(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (disabled || isUploading) return;

    const files = Array.from(e.dataTransfer.files);
    const imageFile = files.find(file => file.type.startsWith('image/'));
    
    if (imageFile) {
      handleFile(imageFile);
    } else {
      setError('Please drop an image file');
    }
  };

  const handleRemoveImage = async () => {
    if (onImageRemove) {
      setIsUploading(true);
      setError(null);
      try {
        await onImageRemove();
        onImageChange(null);
      } catch (error: unknown) {
        console.error('Hero image removal failed:', error);
        setError(error instanceof Error ? error.message : 'Failed to remove image. Please try again.');
      } finally {
        setIsUploading(false);
      }
    } else {
      onImageChange(null);
      setError(null);
    }
  };

  const triggerFileInput = () => {
    if (!disabled && !isUploading) {
      fileInputRef.current?.click();
    }
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Current Image Preview */}
      {currentImageUrl && (
        <div className="relative group">
          <div className="aspect-3/1 rounded-lg overflow-hidden bg-surface-alt border border-border">
            <img
              src={currentImageUrl}
              alt="Club hero image"
              className="w-full h-full object-cover"
            />
            {/* Remove button */}
            <button
              onClick={handleRemoveImage}
              disabled={isUploading}
              className="absolute top-2 right-2 p-2 md:p-1.5 bg-red-600 text-white rounded-full opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity hover:bg-red-700 disabled:opacity-50 touch-manipulation"
              aria-label="Remove hero image"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-xs text-text-muted mt-2">
            Current hero image • Click to replace or remove
          </p>
        </div>
      )}

      {/* Upload Area */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={triggerFileInput}
        className={`
          aspect-3/1 border-2 border-dashed rounded-lg transition-all cursor-pointer
          flex flex-col items-center justify-center text-center p-8
          ${dragActive 
            ? 'border-accent bg-accent/10' 
            : 'border-border hover:border-border-hover hover:bg-surface-alt/50'
          }
          ${disabled || isUploading ? 'cursor-not-allowed opacity-50' : ''}
          ${currentImageUrl ? 'hidden' : ''}
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          disabled={disabled || isUploading}
          className="hidden"
        />

        {isUploading ? (
          <>
            <Loader2 className="w-12 h-12 text-accent animate-spin mb-4" />
            <p className="text-lg font-medium text-text-primary mb-2">Uploading...</p>
            <p className="text-sm text-text-muted">Please wait while we process your image</p>
          </>
        ) : (
          <>
            <div className="p-3 bg-accent/10 rounded-full mb-4">
              {dragActive ? (
                <Upload className="w-8 h-8 text-accent" />
              ) : (
                <ImageIcon className="w-8 h-8 text-accent" />
              )}
            </div>
            
            <p className="text-lg font-medium text-text-primary mb-2">
              {dragActive 
                ? 'Drop your hero image here' 
                : enableCrop 
                  ? 'Upload & Crop Hero Image'
                  : 'Upload Hero Image'
              }
            </p>
            
            <p className="text-sm text-text-muted mb-4">
              {enableCrop 
                ? 'Drag and drop an image or click to browse • Perfect cropping tools included'
                : 'Drag and drop an image or click to browse'
              }
            </p>
            
            <div className="space-y-1 text-xs text-text-muted">
              <p>Recommended: {RECOMMENDED_DIMENSIONS.width} × {RECOMMENDED_DIMENSIONS.height}px</p>
              <p>Maximum file size: 6MB</p>
              <p>Formats: JPG, PNG, WebP</p>
            </div>
          </>
        )}
      </div>

      {/* Replace Button for Existing Image */}
      {currentImageUrl && !isUploading && (
        <div className="flex gap-3">
          <Button
            variant="ghost"
            onClick={triggerFileInput}
            disabled={disabled}
            className="flex-1"
          >
            {enableCrop ? (
              <>
                <CropIcon className="w-4 h-4 mr-2" />
                Replace & Crop
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Replace Image
              </>
            )}
          </Button>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Upload Instructions */}
      {!currentImageUrl && !isUploading && (
        <div className="p-4 bg-surface-alt rounded-lg border border-border">
          <h4 className="text-sm font-medium text-text-primary mb-2">Hero Image Tips:</h4>
          <ul className="text-xs text-text-muted space-y-1">
            <li>• Use high-quality images for best results</li>
            <li>• Landscape orientation works best (3:1 ratio)</li>
            <li>• Ensure important content isn't near the edges</li>
            <li>• Consider how text might appear over your image</li>
          </ul>
        </div>
      )}

      {/* Image Crop Modal */}
      {showCropModal && pendingFile && (
        <ImageCropModal
          isOpen={showCropModal}
          onClose={handleCropCancel}
          imageFile={pendingFile}
          aspectRatio={3} // 3:1 for hero images
          onCropComplete={handleCropComplete}
          maxWidth={RECOMMENDED_DIMENSIONS.width}
          maxHeight={RECOMMENDED_DIMENSIONS.height}
        />
      )}
    </div>
  );
}

export default HeroImageUpload;